// Settings → Google Workspace: connect/disconnect the client-side Google
// integration and configure the shared hub calendar. Rendered inside
// SettingsPage; receives user + updateProfile as props (SSR-testable).
import { useEffect, useState } from 'react'
import { useGoogle } from '../lib/useGoogle.js'
import { connectGoogle, disconnectGoogle, refreshDirectoryProfile } from '../lib/google.js'
import { watchGoogleSettings, saveGoogleSettings } from '../lib/data.js'

const sectionLabel = { color: 'var(--color-gold)' }

export default function GoogleSettingsSection({ user, updateProfile }) {
  const g = useGoogle()
  const [settings, setSettings] = useState(null)
  const [calId, setCalId] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => watchGoogleSettings((s) => {
    setSettings(s)
    setCalId(s?.calendarId || '')
  }), [])

  const connected = g.status === 'connected'
  const busy = g.status === 'connecting'

  const connect = async () => {
    setError(null)
    try {
      await connectGoogle({ hint: user.email })
      // Enrich own Team card from the Workspace Directory — best-effort.
      refreshDirectoryProfile((patch) => updateProfile(patch), { hint: user.email }).catch(() => {})
    } catch (e) {
      setError(e.message)
    }
  }
  const disconnect = () => disconnectGoogle()

  const dirty = calId.trim() !== (settings?.calendarId || '')
  const saveCal = async () => {
    if (!dirty || saving) return
    setSaving(true)
    setError(null)
    try {
      await saveGoogleSettings({ calendarId: calId.trim() }, user.uid)
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <div className="eyebrow" style={{ margin: '16px 0 10px' }}><span className="tag-label" style={sectionLabel}>Google Workspace</span></div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', justifyContent: 'space-between', borderTop: '1px solid var(--color-divider-dark)' }}>
        <div>
          <div style={{ fontSize: 13, color: 'var(--color-white)' }}>
            {connected ? 'Connected — Calendar, Drive, Gmail & Directory' : 'Google access needs reconnecting'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--color-muted)' }}>
            {connected ? 'Access is granted at sign-in. The hub can act as you while this tab is open.' : 'Sign-in normally grants this. Reconnect if access was revoked.'}
          </div>
        </div>
        {connected ? (
          <button onClick={disconnect} className="btn btn-ghost" style={{ fontSize: 12, padding: '8px 14px' }}>Disconnect</button>
        ) : (
          <button onClick={connect} disabled={busy} className="btn btn-gold" style={{ fontSize: 12, padding: '8px 14px', opacity: busy ? 0.45 : 1, cursor: busy ? 'default' : 'pointer' }}>
            {busy ? 'Connecting…' : 'Reconnect'}
          </button>
        )}
      </div>
      <label style={{ display: 'block', marginTop: 6 }}>
        <span className="tag-label" style={{ color: 'var(--color-muted)', display: 'block', marginBottom: 8 }}>Shared calendar ID</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={calId}
            onChange={(e) => setCalId(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && saveCal()}
            placeholder="…@group.calendar.google.com"
            className="cn-input"
            style={{ fontSize: 13, padding: '9px 12px', flex: 1 }}
          />
          <button onClick={saveCal} disabled={!dirty || saving} className="btn btn-gold" style={{ fontSize: 12, padding: '9px 16px', opacity: dirty && !saving ? 1 : 0.45, cursor: dirty && !saving ? 'pointer' : 'default' }}>Save</button>
        </div>
        <span style={{ fontSize: 11, color: 'var(--color-muted)', display: 'block', marginTop: 6 }}>
          Where engagement dates and team OOO land. Create it once — see docs/GOOGLE_SETUP.md.
        </span>
      </label>
      {error && <div style={{ fontSize: 12, color: 'var(--color-danger)', marginTop: 8 }}>{error}</div>}
    </>
  )
}
